/**
 * Enrichment drafts — serialized queue (BB-DRAFTS-UX-01 synthesis, canvas Turn 2b/2c).
 * One focused draft at a time, lowest confidence first; the rest wait as a compact
 * list. Value leads the row and is never truncated (you can't verify a claim you
 * can't read); one primary verb — "Confirm"; Edit is a text link; Discard lives in
 * the ⋯ overflow. Contract §5 still holds: nothing discovered reaches public copy
 * unverified — serializing IS the anti-rubber-stamp mechanism, so no bulk path.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useBrandBibleWizardStore } from '@/stores/brandBibleWizardStore';
import { apiErrorCode } from '@/services/brandBibleV2Service';
import { bbSourcesKeys } from '@/hooks/bbSourcesKeys';
import type { SourceInfo } from '@/hooks/useBbSources';
import type { BbEntry, BbTable } from '@/features/brand-bible-wizard/types';
import { EnvelopeBadge } from '@/components/brand-bible-wizard/primitives';

export interface DraftRow {
  table: BbTable;
  entry: BbEntry;
  /** Short caps label, e.g. "Legal name" / "Hours · Maplewood". */
  label: string;
  /** Rendered value line. */
  value: string;
  /** Source chip text, e.g. "site · footer", "google business profile". */
  sourceLabel: string;
  /** 'field' = one pending identity value (changed only by accept/reject), default 'row'. */
  kind?: 'row' | 'field';
  /** Identity field name for kind 'field', e.g. "phone" or "socials.facebook". */
  field?: string;
  /** Where the web gather found it: link + verbatim quote. Absent for rows without provenance. */
  sources?: SourceInfo[];
}

/** "Questionnaire sheet · A · Services · row 4" -> "Questionnaire, A · Services row 4" (the tab may hold a "·" itself). */
function questionnaireLabel(ref?: string | null): string {
  if (!ref) return 'Client questionnaire';
  const parts = ref.split(' \u00b7 ');
  const rowPart = parts[parts.length - 1] ?? '';
  const tab = parts.slice(1, -1).join(' \u00b7 ');
  return tab && /^row \d+$/.test(rowPart) ? `Questionnaire, ${tab} ${rowPart}` : 'Client questionnaire';
}

/** First source as a link (opens safely) plus its quote as TEXT; "+N more" counts the rest. */
function SourceLine({ sources }: { sources?: SourceInfo[] }) {
  if (!sources || sources.length === 0) return null;
  const [first, ...others] = sources;
  // Only http(s) becomes a link: a javascript:, data: or protocol-relative URL is shown as text.
  const linkable = !!first.url && /^https?:\/\//i.test(first.url);
  const fromQuestionnaire = first.kind === 'questionnaire';
  const linkText = fromQuestionnaire
    ? questionnaireLabel(first.ref)
    : first.kind === 'gbp'
      ? 'Google Business Profile'
      : first.url ?? 'source';
  return (
    <div className="flex flex-col gap-0.5 text-[11.5px] text-muted-foreground">
      <div className="flex flex-wrap items-baseline gap-1.5">
        {linkable && first.url ? (
          <a
            href={first.url}
            target="_blank"
            rel="noopener noreferrer"
            title={fromQuestionnaire ? 'Client questionnaire' : undefined}
            className="font-semibold text-coral hover:underline"
          >
            {linkText}
          </a>
        ) : (
          <span className="font-semibold" title={fromQuestionnaire ? 'Client questionnaire' : undefined}>
            {linkText}
          </span>
        )}
        {!linkable && first.url && !fromQuestionnaire && <span className="break-all font-mono">{first.url}</span>}
        {others.length > 0 && <span data-testid="draft-more-sources">+{others.length} more</span>}
      </div>
      {first.quote && (
        <blockquote data-testid="draft-quote" className="border-l-2 border-border pl-2 italic">
          {first.quote}
        </blockquote>
      )}
    </div>
  );
}

/** One pending identity value: Confirm / Edit (then "Save and confirm") / Discard. */
function PendingFieldRow({ row, canVerify, tenantId: pageTenant }: { row: DraftRow; canVerify: boolean; tenantId: string }) {
  const { acceptPendingField, rejectPendingField, verifyEntry } = useBrandBibleWizardStore();
  // No fallback to the store's tenant: the page names the tenant it renders for, and the store refuses a write without it.
  const tenantForWrites = pageTenant;
  const field = row.field ?? '';
  const queryClient = useQueryClient();
  const tenantId = useBrandBibleWizardStore((s) => s.tenantId);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(row.value);
  const [needsIdentity, setNeedsIdentity] = useState(false);
  const [stale, setStale] = useState(false);

  const run = async (fn: () => Promise<void>, failMsg: string) => {
    setBusy(true);
    setStale(false);
    try {
      await fn();
      setNeedsIdentity(false);
      setEditing(false);
      // accept/reject moves or drops this value's source rows: refetch them
      if (tenantId && field) void queryClient.invalidateQueries({ queryKey: bbSourcesKeys.list(tenantId) });
    } catch (e) {
      const code = apiErrorCode(e);
      if (code === 'identity_unverified') setNeedsIdentity(true);
      else if (code === 'stale') setStale(true);
      else message.error(`${failMsg} — ${e instanceof Error ? e.message : 'request failed'}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li data-testid={`pending-field-${field}`} className="flex flex-col gap-1.5 border-t border-border/60 px-4 py-2.5">
      <div className="flex flex-wrap items-baseline gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{row.label}</span>
        <EnvelopeBadge kind="source">{row.sourceLabel}</EnvelopeBadge>
      </div>
      {editing ? (
        <input
          data-testid="pending-edit-input"
          aria-label={`Edit ${row.label}`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="rounded-[7px] border border-border bg-warm-bg px-2 py-1 text-[13px]"
        />
      ) : (
        <div className="text-[13.5px] font-semibold leading-snug">{row.value}</div>
      )}
      <SourceLine sources={row.sources} />
      {stale && (
        <div data-testid="pending-stale-note" className="text-[11.5px] text-destructive">
          This value changed since you opened it. Check the new value and try again.
        </div>
      )}
      {needsIdentity && (
        <div data-testid="identity-confirm-first" className="flex items-center gap-2 text-[11.5px] text-muted-foreground">
          <span>Confirm identity first.</span>
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(() => verifyEntry('identity', row.entry.id, false, tenantForWrites), 'Confirm failed')}
            className="font-semibold text-coral hover:underline disabled:opacity-60"
          >
            Confirm identity
          </button>
        </div>
      )}
      {canVerify && (
        <div className="flex items-center gap-3">
          {editing ? (
            <>
              <button
                type="button"
                disabled={busy || !text.trim()}
                onClick={() => void run(() => acceptPendingField(field, row.value, text.trim(), tenantForWrites), 'Confirm failed')}
                className="rounded-[7px] bg-coral px-3 py-1.5 text-[11.5px] font-semibold text-white hover:bg-coral-hover disabled:opacity-60"
              >
                Save and confirm
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setText(row.value);
                }}
                className="text-[11.5px] font-semibold text-teal-dark hover:underline"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => void run(() => acceptPendingField(field, row.value, undefined, tenantForWrites), 'Confirm failed')}
                className="rounded-[7px] bg-coral px-3 py-1.5 text-[11.5px] font-semibold text-white hover:bg-coral-hover disabled:opacity-60"
              >
                Confirm
              </button>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="text-[11.5px] font-semibold text-coral hover:underline"
              >
                Edit
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void run(() => rejectPendingField(field, row.value, tenantForWrites), 'Discard failed')}
                className="text-[11.5px] font-semibold text-destructive hover:underline disabled:opacity-60"
              >
                Discard
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
}

/** ONE container for every pending identity value (never an empty card per field). */
function IdentityContainer({ rows, canVerify, tenantId }: { rows: DraftRow[]; canVerify: boolean; tenantId: string }) {
  const entry = rows[0].entry as unknown as Record<string, unknown>;
  const filled = ['legal_name', 'phone', 'email', 'website']
    .map((k) => entry[k])
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '');
  return (
    <div data-testid="identity-container" className="m-3 rounded-[10px] border border-border">
      <div className="px-4 py-2.5">
        <div className="text-[13px] font-semibold">Identity</div>
        <div data-testid="identity-filled" className="text-[12px] text-muted-foreground">
          {filled.join(' · ') || 'Nothing confirmed yet'}
        </div>
      </div>
      <ul>
        {rows.map((r) => (
          <PendingFieldRow key={r.field} row={r} canVerify={canVerify} tenantId={tenantId} />
        ))}
      </ul>
    </div>
  );
}

const CONFIDENCE_ORDER: Record<string, number> = { low: 0, medium: 1, high: 2 };

export function EnrichmentDraftsPanel({
  drafts: allDrafts,
  siteLabel,
  canVerify,
  onConfirmed,
  sectionLabelFor,
  tenantId,
}: {
  drafts: DraftRow[];
  siteLabel: string;
  /** Kept for callers; a draft is read-only until confirmed, so the panel never calls it. */
  onEdit?: (row: DraftRow) => void;
  canVerify: boolean;
  /** Called after a draft is confirmed, so the caller can route the operator to where it landed. */
  onConfirmed?: (row: DraftRow) => void;
  /** Destination label for a draft, e.g. "A · Identity" — shown before the operator acts. */
  sectionLabelFor?: (row: DraftRow) => string;
  /** The page's tenant: store actions refuse when the (global) store has moved to another tenant. */
  tenantId: string;
}) {
  const { verifyEntry, discardEntry } = useBrandBibleWizardStore();
  const fieldRows = allDrafts.filter((d) => d.kind === 'field');
  const drafts = allDrafts.filter((d) => d.kind !== 'field');
  const total = allDrafts.length;
  const tenantForPanel = tenantId;
  const lockedNoteId = useId();
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Session progress: the high-water mark of drafts seen lets "3 of 17 reviewed"
  // survive the list shrinking underneath us.
  const maxSeen = useRef(0);
  const [showDone, setShowDone] = useState(false);
  if (total > maxSeen.current) maxSeen.current = total;

  useEffect(() => {
    if (drafts.length === 0 && maxSeen.current > 0) {
      setShowDone(true);
      const t = setTimeout(() => setShowDone(false), 4000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [total]);

  if (!total) {
    if (!showDone) return null;
    return (
      <div className="flex items-center gap-2 rounded-[10px] border border-[hsl(var(--status-success)/0.3)] bg-[hsl(var(--status-success-bg,140_45%_96%)/0.6)] px-4 py-2.5 text-[12.5px] font-semibold text-[hsl(var(--status-success))]">
        ✓ All {maxSeen.current} drafts reviewed
      </div>
    );
  }

  const identityBlock = fieldRows.length > 0 ? <IdentityContainer rows={fieldRows} canVerify={canVerify} tenantId={tenantId} /> : null;
  if (drafts.length === 0) {
    return (
      <div className="rounded-[10px] border border-border">
        <div className="border-b border-border px-4 py-2.5 text-[13px] font-semibold">
          Enrichment found {maxSeen.current} draft{maxSeen.current === 1 ? '' : 's'}
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            from {siteLabel} · nothing unverified reaches public copy
          </span>
        </div>
        {identityBlock}
      </div>
    );
  }

  const sorted = [...drafts].sort(
    (a, b) =>
      (CONFIDENCE_ORDER[a.entry.confidence ?? 'medium'] ?? 1) -
      (CONFIDENCE_ORDER[b.entry.confidence ?? 'medium'] ?? 1),
  );
  const focused = sorted[0];
  const rest = sorted.slice(1);
  const reviewed = Math.max(0, maxSeen.current - total);
  const progressPct = maxSeen.current > 0 ? (reviewed / maxSeen.current) * 100 : 0;

  const act = async (fn: () => Promise<void>, failMsg: string) => {
    setBusy(true);
    setMenuOpen(false);
    try {
      await fn();
    } catch (e) {
      message.error(`${failMsg} — ${e instanceof Error ? e.message : 'request failed'}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-[10px] border border-border">
      <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-2.5">
        <span className="text-[13px] font-semibold">
          Enrichment found {maxSeen.current} draft{maxSeen.current === 1 ? '' : 's'}
        </span>
        <span className="text-xs text-muted-foreground">
          from {siteLabel} · nothing unverified reaches public copy
        </span>
        <div className="ml-auto flex min-w-[160px] items-center gap-2">
          <span className="whitespace-nowrap text-[11px] text-muted-foreground">
            {reviewed} of {maxSeen.current} reviewed
          </span>
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-coral" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      </div>

      {/* Focused draft — the single decision on screen */}
      <div className="m-3 flex flex-col gap-2 rounded-[10px] border border-coral/35 bg-coral-light/50 px-4 py-3">
        <div className="flex flex-wrap items-baseline gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {focused.label}
          </span>
          {sectionLabelFor && (
            <span className="text-[10.5px] font-semibold text-muted-foreground">
              → {sectionLabelFor(focused)}
            </span>
          )}
          <EnvelopeBadge kind="source">{focused.sourceLabel}</EnvelopeBadge>
          <EnvelopeBadge kind="low">confidence {focused.entry.confidence}</EnvelopeBadge>
        </div>
        <div className="text-[14px] font-semibold leading-snug">{focused.value}</div>
        <SourceLine sources={focused.sources} />
        <div className="relative flex items-center gap-3">
          {canVerify && (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  await verifyEntry(focused.table, focused.entry.id, false, tenantForPanel);
                  onConfirmed?.(focused);
                }, 'Confirm failed')
              }
              className="rounded-[7px] bg-coral px-3 py-1.5 text-[11.5px] font-semibold text-white hover:bg-coral-hover disabled:opacity-60"
            >
              Confirm
            </button>
          )}
          <button
            type="button"
            disabled
            aria-disabled="true"
            aria-describedby={lockedNoteId}
            data-testid="draft-edit-locked"
            className="text-[11.5px] font-semibold text-muted-foreground"
          >
            Confirm to edit
          </button>
          <span id={lockedNoteId} data-testid="draft-edit-locked-note" className="text-[11px] text-muted-foreground">
            A draft is read-only. Confirm it first, then you can edit it.
          </span>
          <button
            type="button"
            aria-label="More actions"
            onClick={() => setMenuOpen((o) => !o)}
            className="px-1.5 text-[13px] font-bold tracking-widest text-teal-dark"
          >
            ⋯
          </button>
          {menuOpen && (
            <div className="absolute left-24 top-7 z-10 rounded-[8px] border border-border bg-warm-bg shadow-md">
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void act(() => discardEntry(focused.table, focused.entry.id, tenantForPanel), 'Discard failed')
                }
                className="px-3.5 py-2 text-[11.5px] font-semibold text-destructive disabled:opacity-60"
              >
                Discard draft
              </button>
            </div>
          )}
          {rest.length > 0 && (
            <span className="ml-auto text-[11px] text-muted-foreground">
              confirming advances to the next draft · lowest confidence first
            </span>
          )}
        </div>
      </div>

      {/* The rest wait quietly — no actions until focused */}
      {rest.length > 0 && (
        <ul className="border-t border-border/60">
          {rest.slice(0, 4).map((row) => (
            <li
              key={`${row.table}:${row.entry.id}`}
              className="flex items-center gap-2.5 border-b border-border/40 px-4 py-2 text-[12.5px] text-muted-foreground last:border-b-0"
            >
              <EnvelopeBadge kind="low">{(row.entry.confidence ?? 'med').slice(0, 3)}</EnvelopeBadge>
              <span className="truncate">{row.value}</span>
            </li>
          ))}
          {rest.length > 4 && (
            <li className="px-4 py-2 text-[12px] text-muted-foreground/70">
              {rest.length - 4} more waiting…
            </li>
          )}
        </ul>
      )}
      {identityBlock}
    </div>
  );
}
